import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Plus, 
  MoreVertical, 
  Star,
  Download,
  Trash2,
  Edit
} from 'lucide-react';
import { CreateWidgetModal } from './CreateWidgetModal';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

// Dummy data for widgets
const widgetData = {
  'highest-elec-cost': {
    type: 'bar',
    title: 'Highest Elec Cost Locations',
    data: [
      { location: 'Autumn Hills Apar...', cost: 24000 },
      { location: 'Savannah Heights', cost: 6000 },
      { location: '11503 Springfield...', cost: 6000 },
      { location: 'Woodmere Apartme...', cost: 4500 },
      { location: 'South Terrace Apa...', cost: 3000 },
      { location: 'North Pointe Town...', cost: 3000 },
      { location: 'Clearwater Apartm...', cost: 2500 },
      { location: 'Waterbury Apartme...', cost: 2000 },
      { location: 'Village Square', cost: 1800 },
      { location: 'Forest Meadows', cost: 1500 },
      { location: 'Southland Greens', cost: 1200 },
      { location: 'Meadowood Apartm...', cost: 1000 },
    ],
  },
  'costs-by-utility': {
    type: 'pie',
    title: 'Costs by Utility Type',
    data: [
      { name: 'Sewer', value: 37, color: '#3b82f6' },
      { name: 'Water', value: 26, color: '#1e40af' },
      { name: 'Refuse', value: 15, color: '#eab308' },
      { name: 'Electric', value: 12, color: '#84cc16' },
      { name: 'Naturalgas', value: 8, color: '#1e3a8a' },
      { name: 'Other', value: 2, color: '#9ca3af' },
    ],
  },
  'highest-unit-cost': {
    type: 'bar',
    title: 'Highest Unit Cost Locations',
    data: [
      { location: '6224 Cheviot Rd A...', cost: 15 },
      { location: '24 E North St Uni...', cost: 2.5 },
      { location: '2098 Quail Ct Apt...', cost: 1.8 },
      { location: '9339 Triangle Dr', cost: 1.5 },
      { location: '2074 Quail Ct Apt. #4', cost: 1.2 },
      { location: '307 Pearl St #1', cost: 1 },
      { location: '6222 Cheviot Rd A...', cost: 0.8 },
      { location: '9353 Roundtop Roa...', cost: 0.7 },
      { location: '14 Koehler Ave Ap...', cost: 0.6 },
      { location: '2027 Waycross Rd #1', cost: 0.5 },
      { location: '5850 Pameleen Ct', cost: 0.4 },
      { location: '2063 Waycross Rd #3', cost: 0.4 },
    ],
    yAxisLabel: '$/KWH',
  },
  'costs-by-charge-type': {
    type: 'stacked-bar',
    title: 'Costs by Charge Type',
    data: [
      { month: 'Jan', commodity: 800, customer: 0, other: 0, consumption: 0, taxes: 0 },
      { month: 'Feb', commodity: 1200, customer: 100, other: 50, consumption: 150, taxes: 0 },
      { month: 'Mar', commodity: 6000, customer: 800, other: 500, consumption: 4000, taxes: 200 },
      { month: 'Apr', commodity: 1800, customer: 200, other: 100, consumption: 400, taxes: 0 },
      { month: 'May', commodity: 700, customer: 0, other: 0, consumption: 0, taxes: 0 },
      { month: 'Jun', commodity: 800, customer: 0, other: 0, consumption: 0, taxes: 0 },
      { month: 'Jul', commodity: 1800, customer: 200, other: 100, consumption: 400, taxes: 0 },
      { month: 'Aug', commodity: 5800, customer: 700, other: 450, consumption: 3800, taxes: 250 },
      { month: 'Sep', commodity: 5500, customer: 650, other: 400, consumption: 3600, taxes: 350 },
      { month: 'Oct', commodity: 7000, customer: 800, other: 500, consumption: 4500, taxes: 700 },
      { month: 'Nov', commodity: 3500, customer: 400, other: 250, consumption: 1200, taxes: 150 },
      { month: 'Dec', commodity: 700, customer: 0, other: 0, consumption: 0, taxes: 0 },
    ],
  },
  'highest-gas-cost': {
    type: 'bar',
    title: 'Highest Gas Cost Locations',
    data: [
      { location: 'South Terrace Apa...', cost: 18500 },
      { location: 'Clearwater Apartm...', cost: 11000 },
      { location: 'Groesbeck Gardens', cost: 5000 },
      { location: 'Amberley House Ap...', cost: 5000 },
      { location: '307 Pearl St', cost: 2000 },
      { location: 'Southland Greens', cost: 2000 },
      { location: 'Autumn Hills Apar...', cost: 1500 },
      { location: '11503 Springfield...', cost: 1500 },
      { location: 'North Pointe Town...', cost: 1000 },
      { location: '2905 Banning Road...', cost: 500 },
      { location: '2903 Banning Road...', cost: 500 },
      { location: '9282 Roundtop Road', cost: 500 },
    ],
  },
  'charges-by-vendor': {
    type: 'bar',
    title: 'Charges by Vendor',
    data: [
      { vendor: 'Greater Cincinnat...', cost: 270000 },
      { vendor: 'Duke Energy', cost: 115000 },
      { vendor: 'Rumpke', cost: 85000 },
      { vendor: 'City of Reading W....', cost: 60000 },
      { vendor: 'Butler County Wat...', cost: 20000 },
      { vendor: 'City of Franklin', cost: 15000 },
      { vendor: 'Clermont County W...', cost: 10000 },
      { vendor: 'Forest Park Storm...', cost: 5000 },
    ],
  },
};

interface Widget {
  id: string;
  type: string;
  title: string;
  isStarred?: boolean;
}

const initialWidgets: Widget[] = [
  { id: 'highest-elec-cost', type: 'bar', title: 'Highest Elec Cost Locations', isStarred: true },
  { id: 'costs-by-utility', type: 'pie', title: 'Costs by Utility Type', isStarred: true },
  { id: 'highest-unit-cost', type: 'bar', title: 'Highest Unit Cost Locations' },
  { id: 'costs-by-charge-type', type: 'stacked-bar', title: 'Costs by Charge Type' },
  { id: 'highest-gas-cost', type: 'bar', title: 'Highest Gas Cost Locations' },
  { id: 'charges-by-vendor', type: 'bar', title: 'Charges by Vendor' },
];

export function PortfolioView() {
  const [widgets, setWidgets] = useState<Widget[]>(initialWidgets);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const toggleStar = (widgetId: string) => {
    setWidgets(widgets.map(w => 
      w.id === widgetId ? { ...w, isStarred: !w.isStarred } : w
    ));
  };

  const deleteWidget = (widgetId: string) => {
    setWidgets(widgets.filter(w => w.id !== widgetId));
  };

  const renderWidget = (widget: Widget) => {
    const data = widgetData[widget.id as keyof typeof widgetData];
    if (!data) return null;

    if (data.type === 'pie') {
      return (
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data.data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${value}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {data.data.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      );
    }

    if (data.type === 'stacked-bar') {
      return (
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="commodity" stackId="a" fill="#3b82f6" name="Commodity Charges" />
              <Bar dataKey="customer" stackId="a" fill="#eab308" name="Customer Charges" />
              <Bar dataKey="other" stackId="a" fill="#1e40af" name="Other Charges" />
              <Bar dataKey="consumption" stackId="a" fill="#1e3a8a" name="Consumption Charges" />
              <Bar dataKey="taxes" stackId="a" fill="#16a34a" name="Taxes Charges" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    }

    // Regular bar chart
    return (
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey={data.data[0]?.location ? 'location' : 'vendor'} 
              angle={-45}
              textAnchor="end"
              height={100}
            />
            <YAxis label={{ value: data.yAxisLabel || 'USD', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Bar dataKey="cost" fill="#3b82f6" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Portfolio View</h2>
          <p className="text-muted-foreground">Visualize your utility data with customizable widgets</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Create Widget
        </Button>
      </div>

      {/* Widgets Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {widgets.map((widget) => {
          const data = widgetData[widget.id as keyof typeof widgetData];
          return (
            <Card key={widget.id} className="border-l-4 border-l-primary/50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => toggleStar(widget.id)}
                    >
                      <Star 
                        className={`h-4 w-4 ${widget.isStarred ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`} 
                      />
                    </Button>
                    <CardTitle className="text-base">{widget.title}</CardTitle>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Download className="h-4 w-4 mr-2" />
                        Export
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="text-destructive"
                        onClick={() => deleteWidget(widget.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                {data ? renderWidget(widget) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create Widget Modal */}
      <CreateWidgetModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onWidgetCreated={(widgetType) => {
          // Add new widget - map widget type to actual widget data
          const widgetIdMap: Record<string, string> = {
            'Costs by Utility Type': 'costs-by-utility',
            'Charges by Vendor': 'charges-by-vendor',
            'Costs by Charge Type': 'costs-by-charge-type',
            'Highest Cost Locations': 'highest-elec-cost',
            'Highest Unit Cost Locations': 'highest-unit-cost',
            'Highest Gas Cost Locations': 'highest-gas-cost',
          };
          
          const widgetId = widgetIdMap[widgetType] || `widget-${Date.now()}`;
          const newWidget: Widget = {
            id: widgetId,
            type: widgetType.toLowerCase().replace(/\s+/g, '-'),
            title: widgetType,
          };
          setWidgets([...widgets, newWidget]);
          setShowCreateModal(false);
        }}
      />
    </div>
  );
}

